(() => {
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(root) {
    const cards = [...root.querySelectorAll('[data-rose-film-open]')];
    const strip = root.querySelector('#rose-films-strip');
    const stripCards = [...root.querySelectorAll('.rose-films-card')];
    const previousCard = root.querySelector('[data-rose-films-direction="-1"]');
    const nextCard = root.querySelector('[data-rose-films-direction="1"]');
    const previews = [...root.querySelectorAll('[data-rose-film-preview]')];
    const dialog = root.querySelector('[data-rose-film-dialog]');
    const stage = root.querySelector('[data-rose-film-stage]');
    const players = [...root.querySelectorAll('[data-rose-film-player]')];
    const close = root.querySelector('[data-rose-film-close]');
    const title = root.querySelector('[data-rose-film-title]');
    const count = root.querySelector('[data-rose-film-count]');
    const retry = root.querySelector('[data-rose-film-retry]');
    if (!cards.length || !strip || !previousCard || !nextCard || !dialog || !stage || players.length !== 2 || !close || !title || !count || !retry) return;

    function nearestCardIndex() {
      return stripCards.reduce((nearest, card, index) =>
        Math.abs(card.offsetLeft - stripCards[0].offsetLeft - strip.scrollLeft) < Math.abs(stripCards[nearest].offsetLeft - stripCards[0].offsetLeft - strip.scrollLeft) ? index : nearest, 0);
    }

    function updateCardControls() {
      const maxScroll = Math.max(0, strip.scrollWidth - strip.clientWidth);
      previousCard.disabled = strip.scrollLeft <= 3;
      nextCard.disabled = strip.scrollLeft >= maxScroll - 3;
    }

    function moveCards(direction) {
      if (stripCards.length < 2) return;
      const index = Math.max(0, Math.min(stripCards.length - 1, nearestCardIndex() + direction));
      strip.scrollTo({
        left: stripCards[index].offsetLeft - stripCards[0].offsetLeft,
        behavior: motion.matches ? 'auto' : 'smooth'
      });
    }

    previousCard.addEventListener('click', () => moveCards(-1));
    nextCard.addEventListener('click', () => moveCards(1));
    strip.addEventListener('keydown', event => {
      if (event.target !== strip || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      moveCards(event.key === 'ArrowRight' ? 1 : -1);
    });
    strip.addEventListener('scroll', updateCardControls, {passive:true});
    window.addEventListener('resize', updateCardControls);
    updateCardControls();

    let active = -1;
    let epoch = 0;
    let opener = null;
    let bodyOverflow = '';
    let rootOverflow = '';
    let touchStartY = null;
    let lastWheelNavigation = 0;
    let visiblePlayer = null;
    let pendingPlayer = null;
    let retryPlayback = null;
    let transitionTimer = 0;
    let transitioning = false;
    const visiblePreviews = new WeakSet();
    const dataSaver = navigator.connection;

    function updatePreviews() {
      const canRun = !dialog.open && !document.hidden && !motion.matches && !dataSaver?.saveData;
      previews.forEach(video => {
        if (!canRun || !visiblePreviews.has(video)) {
          video.pause();
          if (motion.matches || dataSaver?.saveData) {
            video.removeAttribute('src');
            video.load();
          }
          return;
        }
        if (!video.src && video.dataset.previewSrc) video.src = video.dataset.previewSrc;
        video.muted = true;
        video.play().catch(() => {});
      });
    }

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio >= .25) visiblePreviews.add(entry.target);
          else visiblePreviews.delete(entry.target);
        });
        updatePreviews();
      }, {threshold:[0,.25]})
      : null;
    previews.forEach(video => observer?.observe(video));

    function resetPlayer(video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.className = 'rose-film-player';
    }

    function setLabels(index) {
      const name = cards[index].dataset.title || '';
      title.textContent = name;
      count.textContent = `${String(index + 1).padStart(2,'0')} / ${String(cards.length).padStart(2,'0')}`;
      dialog.setAttribute('aria-label', `Témoignage OSE Thérapies : ${name}`);
    }

    function show(index) {
      if (!dialog.open || transitioning) return;
      const direction = active < 0 || index >= active ? 1 : -1;
      active = (index + cards.length) % cards.length;
      const card = cards[active];
      const token = ++epoch;
      retry.hidden = true;
      retryPlayback = null;
      if (pendingPlayer) resetPlayer(pendingPlayer);
      const incoming = players.find(video => video !== visiblePlayer);
      pendingPlayer = incoming;
      incoming.poster = card.dataset.poster || '';
      incoming.src = card.dataset.fullSrc || '';
      incoming.muted = true;
      incoming.load();

      const firstVideo = !visiblePlayer;
      if (firstVideo) {
        incoming.classList.add('is-current','is-on-top');
        visiblePlayer = incoming;
        pendingPlayer = null;
        setLabels(active);
      }

      const start = () => {
        if (token !== epoch || !dialog.open) return;
        Promise.resolve(incoming.play()).then(() => {
          if (token !== epoch || !dialog.open) return;
          if (firstVideo) {
            incoming.muted = false;
            return;
          }
          const outgoing = visiblePlayer;
          outgoing.pause();
          outgoing.muted = true;
          incoming.muted = false;
          incoming.classList.add(direction > 0 ? 'is-staging-next' : 'is-staging-prev','is-on-top');
          void incoming.offsetWidth;
          incoming.classList.add('is-current');
          incoming.classList.remove('is-staging-next','is-staging-prev');
          visiblePlayer = incoming;
          pendingPlayer = null;
          transitioning = true;
          setLabels(active);
          transitionTimer = window.setTimeout(() => {
            if (token !== epoch) return;
            resetPlayer(outgoing);
            incoming.classList.remove('is-on-top');
            transitioning = false;
          }, motion.matches ? 0 : 510);
        }).catch(error => {
          if (token === epoch && dialog.open && error?.name !== 'AbortError') retry.hidden = false;
        });
      };
      retryPlayback = start;
      if (incoming.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) start();
      else incoming.addEventListener('loadeddata', start, {once:true});
    }

    function open(index, trigger) {
      if (dialog.open) return;
      opener = trigger;
      bodyOverflow = document.body.style.overflow;
      rootOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      try {
        dialog.showModal();
      } catch (_) {
        document.body.style.overflow = bodyOverflow;
        document.documentElement.style.overflow = rootOverflow;
        return;
      }
      updatePreviews();
      show(index);
      close.focus({preventScroll:true});
    }

    function closeViewer() {
      if (dialog.open) dialog.close();
    }

    cards.forEach((card,index) => card.addEventListener('click', () => open(index,card)));
    close.addEventListener('click',closeViewer);
    dialog.addEventListener('cancel',event => {event.preventDefault();closeViewer();});
    dialog.addEventListener('close',() => {
      ++epoch;
      window.clearTimeout(transitionTimer);
      players.forEach(resetPlayer);
      visiblePlayer = null;
      pendingPlayer = null;
      retryPlayback = null;
      transitioning = false;
      retry.hidden = true;
      active = -1;
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = rootOverflow;
      updatePreviews();
      if (opener?.isConnected && !document.hidden) opener.focus({preventScroll:true});
      opener = null;
    });
    dialog.addEventListener('keydown',event => {
      if (event.key === 'Escape') {event.preventDefault();closeViewer();return;}
      const direction = ['ArrowDown','ArrowRight','PageDown'].includes(event.key) ? 1
        : ['ArrowUp','ArrowLeft','PageUp'].includes(event.key) ? -1 : 0;
      if (!direction) return;
      event.preventDefault();
      show(active + direction);
    });
    stage.addEventListener('wheel',event => {
      if (!dialog.open || Math.abs(event.deltaY) < 15) return;
      event.preventDefault();
      const now = performance.now();
      if (now - lastWheelNavigation < 550) return;
      lastWheelNavigation = now;
      show(active + Math.sign(event.deltaY));
    },{passive:false});
    stage.addEventListener('touchstart',event => {
      touchStartY = event.touches.length === 1 ? event.touches[0].clientY : null;
    },{passive:true});
    stage.addEventListener('touchend',event => {
      if (!dialog.open || touchStartY === null || event.changedTouches.length !== 1) return;
      const distance = touchStartY - event.changedTouches[0].clientY;
      touchStartY = null;
      if (Math.abs(distance) >= 55) show(active + Math.sign(distance));
    },{passive:true});
    players.forEach(video => {
      video.addEventListener('ended',() => {
        if (video === visiblePlayer && !pendingPlayer && !transitioning) show(active + 1);
      });
      video.addEventListener('error',() => {
        if (dialog.open && (video === pendingPlayer || video === visiblePlayer)) retry.hidden = false;
      });
    });
    retry.querySelector('button')?.addEventListener('click',() => {
      retry.hidden = true;
      retryPlayback?.();
    });
    document.addEventListener('visibilitychange',() => {
      if (document.hidden) closeViewer();
      updatePreviews();
    });
    motion.addEventListener?.('change',updatePreviews);
    dataSaver?.addEventListener?.('change',updatePreviews);
    if (!observer) {
      previews.forEach(video => visiblePreviews.add(video));
      updatePreviews();
    }
  }

  function boot() {
    document.querySelectorAll('[data-rose-films]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',boot,{once:true});
  else boot();
})();
